import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { useDrop } from "react-dnd";
import { MultiSignerUpdate, RequestParticipant } from "models/interfaces";
import { updateMultipleSigners, updateSignerOrders } from "redux/appSlice";
import { getGroupDelay, normalizeOrders } from "utils/signerOrderUtils";
import approversAndSignersStyles from "../ApproversAndSigners.styles";

const ItemType = {
  SIGNER: "signer",
};

// IMPORTANT: This is a top-level component (stable identity).
// Defining DnD wrappers inside `ApproversAndSigners` causes React to treat them
// as new component types on each re-render (each keystroke), forcing
// unmount/mount and focus loss.
type StableSignerDropContainerProps = {
  /** The `order` shared by every signer in this level. */
  orderKey: number | string;
  children: React.ReactNode;
  signers: RequestParticipant[];
  dispatch: any;
  forBulkSend?: boolean;
};

/**
 * One signing level, and the drop target for it. Matches the design's
 * `dragOverGroup` / `dropOnGroup` pair: hovering only highlights the level, and
 * dropping moves the dragged signer *into* it — levels are never reordered
 * mid-drag (the level header arrows do that).
 */
export const StableSignerDropContainer = React.memo(
  function StableSignerDropContainer({
    orderKey,
    children,
    signers,
    dispatch,
    forBulkSend = false,
  }: StableSignerDropContainerProps) {
    const dropRef = useRef<HTMLDivElement | null>(null);

    const [{ isOver }, drop] = useDrop({
      accept: ItemType.SIGNER,
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
      }),
      drop: (dragged: any) => {
        const signerId = dragged.id ?? dragged.signerId;
        if (signerId === undefined) return;

        const targetOrder = Number(orderKey);
        const source = signers.find((s) => s.id === signerId);
        if (!source || Number(source.order) === targetOrder) return;

        // A level's wait belongs to the level, so the target's own delay wins;
        // a signer arriving from elsewhere brings its delay only if the target
        // has none.
        const targetDelay = getGroupDelay(signers, targetOrder);
        const movingDelay =
          source.delayDays || source.delayHours || source.scheduleTime
            ? {
                delayDays: source.delayDays,
                delayHours: source.delayHours,
                scheduleTime: source.scheduleTime,
              }
            : undefined;
        const finalDelay = targetDelay ?? movingDelay;

        const normalized = normalizeOrders(
          signers.map((s) =>
            s.id === signerId ? { ...s, order: targetOrder } : s,
          ),
        );
        dispatch(updateSignerOrders(normalized));

        const committedOrder = normalized.find((s) => s.id === signerId)?.order;
        if (committedOrder == null) return;

        const updates: MultiSignerUpdate[] = normalized
          .filter((s) => s.order === committedOrder)
          .map((member) => ({
            signerId: member.id,
            // Level 1 can never hold a wait.
            fieldsToUpdate:
              committedOrder === 1 || !finalDelay
                ? {
                    delayDays: undefined,
                    delayHours: undefined,
                    scheduleTime: undefined,
                  }
                : finalDelay,
          }));

        // Anything that shifted into level 1 loses its wait too.
        normalized
          .filter(
            (s) =>
              s.order === 1 &&
              s.order !== committedOrder &&
              (s.delayDays || s.delayHours || s.scheduleTime),
          )
          .forEach((s) =>
            updates.push({
              signerId: s.id,
              fieldsToUpdate: {
                delayDays: undefined,
                delayHours: undefined,
                scheduleTime: undefined,
              },
            }),
          );

        if (updates.length) dispatch(updateMultipleSigners(updates));
      },
    });

    useEffect(() => {
      if (dropRef.current) drop(dropRef.current);
    }, [drop]);

    return (
      <Box
        ref={dropRef}
        sx={{
          ...(approversAndSignersStyles.levelGroup(isOver) as object),
          cursor: forBulkSend ? "auto" : "default",
          width: "100%",
        }}
      >
        {children}
      </Box>
    );
  },
);
